import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { simulationV2 } from '~/services/simulation-service';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { useStoreActions, useStoreState } from '~/store';
import { resolveSimulationServiceVersion } from '~/store/simulation/resolveSimulationServiceVersion';
import type { SimulationServiceV1SimulationReportsDetailParams } from '@shipium-corp/shipium-ts-types';
import type {
  FetchSimulationReportParams,
  SimulationDetail,
  SimulationReport,
  SimulationReportType,
} from '~/store/simulation/types';

export interface ReportFilterQueryParams {
  tntType?: string;
  carrier?: string;
  dayOfWeek?: string;
  tenantId?: string;
  originSlug?: string;
}

/** Shared report-filter search-param parsing (keys as literals to avoid a tab<->hook circular import). */
export function useReportFilterParams(): ReportFilterQueryParams {
  const [query] = useSearchParams();
  return useMemo(
    () => ({
      tntType: query.get('tntType') || undefined,
      carrier: query.get('carrier') || undefined,
      dayOfWeek: query.get('dayOfWeek') || undefined,
      tenantId: query.get('tenantId') || undefined,
      originSlug: query.get('originSlug') || undefined,
    }),
    [query],
  );
}

// otd-report and cost-adjustment-report are v2-served but not yet in the generated request union;
// widen until ts-types catches up.
const V2_REPORT_TYPES: ReadonlySet<string> = new Set<
  SimulationServiceV1SimulationReportsDetailParams['report'] | 'otd-report' | 'cost-adjustment-report'
>([
  'carrier-selection-report',
  'time-in-transit-report',
  'simulation-choropleth-report',
  'reporting-filters',
  'otd-report',
  'cost-adjustment-report',
]);

export type ReportQueryParams = Omit<FetchSimulationReportParams, 'simulationId' | 'reportType'>;

/**
 * Version-blind report read: v2 (ported types) via React Query, else the existing thunk.
 * `params` must be a stable ref — it drives the v2 query key and the v1 fetch effect.
 */
export function useSimulationReport(
  simulation: SimulationDetail,
  reportType: SimulationReportType,
  params?: ReportQueryParams,
): { reportData: SimulationReport['reportData']; loading: boolean } {
  const simulationId = simulation.simulationId;
  const isComplete = simulation.simulationStatus === 'completed';
  const version = resolveSimulationServiceVersion(simulation.simulationSetupData);
  const useV2 = version === 'v2' && V2_REPORT_TYPES.has(reportType);

  const fullParams: FetchSimulationReportParams = { simulationId, reportType, ...params };

  const fetchSimulationReport = useStoreActions((actions) => actions.simulation.fetchSimulationReport);
  const getSimulationReport = useStoreState((state) => state.simulation.getSimulationReport);
  const storeLoading = useStoreState((state) => state.loading.loadingFlags.simulation);
  const dispatchMessage = useStoreActions((actions) => actions.messages.dispatchMessage);

  const v2Query = useQuery({
    ...useQueryCreator(simulationV2.report, { args: [fullParams] }),
    enabled: useV2 && isComplete && !!simulationId,
    // match the v1 thunk's failure notification
    onError: () => dispatchMessage({ noticeType: 'error', content: 'Error loading report. Please retry.' }),
  });

  useEffect(() => {
    // Only real v1 sims hit the v1 thunk. A v2 sim viewing an unported report (e.g. otd-report)
    // has no v1 record — firing here would 404 and toast; it falls through to the empty state.
    if (version === 'v1' && isComplete && simulationId) {
      fetchSimulationReport(fullParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fullParams tracked via the stable `params` ref
  }, [version, isComplete, simulationId, fetchSimulationReport, params]);

  if (useV2) {
    return { reportData: v2Query.data?.reportData, loading: v2Query.isLoading };
  }
  return { reportData: getSimulationReport(simulationId, reportType)?.reportData, loading: !!storeLoading };
}
